'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';

const STRIPS = 36;

// Pleated vertical light streaks, drawn in CSS so they stay sharp at any size.
// GSAP drives a diagonal wave of light that rolls across the pleats.
export default function OurWorkStreaks() {
    const root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = root.current;
        if (!el) return;
        const glows = gsap.utils.toArray<HTMLElement>('.ow-glow', el);
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const place = (phase: number) => {
            glows.forEach((g, i) => {
                const t = i / STRIPS;
                // Band falls from top-left to bottom-right, with a gentle ripple
                const y = -20 + t * 55 + Math.sin(phase + i * 0.42) * 9;
                const glow = 0.7 + 0.3 * Math.sin(phase * 1.3 + i * 0.3);
                gsap.set(g, { yPercent: y, opacity: glow });
            });
        };

        const ctx = gsap.context(() => {
            gsap.from(el, { opacity: 0, duration: 1.6, ease: 'power2.out' });
            const state = { phase: 0 };
            place(0);
            if (!reduce) {
                gsap.to(state, {
                    phase: Math.PI * 2,
                    duration: 14,
                    ease: 'none',
                    repeat: -1,
                    onUpdate: () => place(state.phase),
                });
            }
        }, el);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={root} aria-hidden className="absolute inset-0 -z-20 overflow-hidden bg-[#03040a]">
            {/* Deep colour wash behind the pleats */}
            <div className="absolute -left-[10%] top-[10%] h-[60%] w-[60%] rounded-full bg-[#1e3a8a]/60 blur-[120px]" />
            <div className="absolute right-[5%] top-[-10%] h-[70%] w-[45%] rounded-full bg-[#1d26c9]/55 blur-[120px]" />

            <div className="absolute inset-0 flex">
                {Array.from({ length: STRIPS }, (_, i) => (
                    <div
                        key={i}
                        className="relative h-full flex-1 overflow-hidden"
                        style={{
                            // Folded edge: dark sides, faint ridge in the middle
                            background:
                                'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 38%, rgba(255,255,255,0.035) 50%, rgba(0,0,0,0) 62%, rgba(0,0,0,0.55) 100%)',
                        }}
                    >
                        <div
                            className="ow-glow absolute inset-x-0 top-0 h-[90%] will-change-transform"
                            style={{
                                background:
                                    'linear-gradient(180deg, transparent 0%, rgba(29,78,216,0.7) 20%, rgba(96,165,250,1) 44%, rgba(239,246,255,1) 52%, rgba(96,165,250,0.9) 60%, rgba(37,99,235,0.6) 78%, transparent 100%)',
                                // Taper to a thin bright ridge so each pleat reads crisp
                                maskImage:
                                    'linear-gradient(90deg, transparent 4%, rgba(0,0,0,0.6) 28%, #000 50%, rgba(0,0,0,0.6) 72%, transparent 96%)',
                                WebkitMaskImage:
                                    'linear-gradient(90deg, transparent 4%, rgba(0,0,0,0.6) 28%, #000 50%, rgba(0,0,0,0.6) 72%, transparent 96%)',
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* Vignette to keep the edges deep */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(3,4,10,0.55)_100%)]" />
        </div>
    );
}
